import DebtorChip from "@/components/shared/debtor-chip";
import { create_error_code_toast } from "@/components/shared/error-code-toast";
import { clientFetch, url } from "@/components/shared/fetch-client/fetch_client";
import TableBase, { TableIdEnum } from "@/components/shared/table/table-base";
import { format_amount_handler } from "@/components/shared/utils";
import i18n from "@/i18n";
import { useTheme } from "@emotion/react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DateRangeIcon from "@mui/icons-material/DateRange";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import PersonIcon from "@mui/icons-material/Person";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
export default function OrderTable({
	debtor_id = null,
	is_back_office = false,
	url_params_enabled,
}) {
	// Hooks
	const { t } = useTranslation();
	const [count, setCount] = useState(0);
	const [rows, setRows] = useState([]);
	const tableRef = useRef(null);

	const onChangeHandler = async (params, setLoading) => {
		// Make API call
		let uri = url`/api/billing/order`;

		if (is_back_office) {
			uri = url`/api/back-office/order`;
		}

		if (is_back_office && debtor_id) {
			uri = url`/api/back-office/debtor/${debtor_id}/orders`;
		}

		// Make fetch call
		try {
			const { ok, body } = await clientFetch(uri, {
				params: { ...params },
			}).get();

			if (!ok) {
				return null;
			}

			setCount(body.count);
			setRows(body.data);
		} catch (error) {
			create_error_code_toast(t, error);
		} finally {
			setLoading(false);
		}
	};

	const additionalFilters = [
		{
			key: "order_status",
			label: t("billing.orders.table.status.label"),
			option_list: [
				{
					label: t("billing.orders.table.status.active"),
					value: "active",
				},
				{
					label: t("billing.orders.table.status.replaced"),
					value: "replaced",
				},
				{
					label: t("billing.orders.table.status.completed"),
					value: "completed",
				},
				{
					label: t("billing.orders.table.status.cancellation_pending"),
					value: "cancellation_pending",
				},
				{
					label: t("billing.orders.table.status.pending"),
					value: "pending",
				},
				{
					label: t("billing.orders.table.status.ended"),
					value: "ended",
				},
				{
					label: t("billing.orders.table.status.cancelled"),
					value: "cancelled",
				},
				{
					label: t("billing.orders.table.status.failed"),
					value: "failed",
				},
			],
			type: "select",
		},
	];

	if (is_back_office) {
		additionalFilters.unshift({
			key: "order_type",
			label: t("billing.orders.table.order_type.label"),
			option_list: [
				{
					label: t("billing.orders.table.order_type.domain"),
					value: "domain",
				},
				{
					label: t("billing.orders.table.order_type.pbs"),
					value: "pbs",
				},
				{
					label: t("billing.orders.table.order_type.pbs_free"),
					value: "pbs_free",
				},
				{
					label: t("billing.orders.table.order_type.pbs_sync"),
					value: "pbs_sync",
				},
				{
					label: t("billing.orders.table.order_type.qemu"),
					value: "qemu",
				},
			],
			type: "select",
		});
	}

	return (
		<Box sx={{ my: 3 }}>
			<TableBase
				url_params_enabled={url_params_enabled}
				ref={tableRef}
				enable_search
				enable_reset_button
				nested_tables
				sort_default="desc"
				order_by_default="order_code"
				on_change={onChangeHandler}
				total_count={count}
				table_id={TableIdEnum.ORDER_TABLE}
				additional_filters={additionalFilters}
				column_list={[
					{
						key: "order_status",
						label: t("billing.orders.table.order_status"),
					},
					{
						key: "order_code",
						label: t("billing.orders.table.order_number"),
					},
					{
						hidden: !is_back_office,
						key: "debtor_code",
						label: t("billing.orders.table.debtor_code"),
					},
					{
						hidden: !is_back_office,
						key: "company_name",
						label: t("billing.orders.table.company"),
					},
					{
						key: "created_at",
						label: t("billing.orders.table.order_date"),
					},
					{
						key: "description",
						label: t("billing.orders.table.order_label"),
					},
					{
						key: "total",
						label: t("billing.orders.table.total_price"),
					},
				]}
				row_list={rows.map((row) => ({
					cell_list: [
						{
							key: "order_status",
							value: <DetermOrderStatusChip order_status={row.order_status} />,
						},
						{
							key: "order_code",
							value: row.order_code,
						},
						{
							key: "debtor_code",
							value: (
								<DebtorChip
									debtor_code={row.debtor_code}
									debtor_id={row.debtor_id}
									is_back_office={is_back_office}
								/>
							),
						},
						{
							key: "company_name",
							value: row?.company_name || "-",
						},
						{
							key: "created_at",
							value: new Date(row.created_at).toLocaleString(),
						},
						{
							key: "description",
							value: <DetermProductRedirectLink row={row} />,
						},
						{
							key: "total",
							value: <DetermOrderTotal row={row} />,
						},
					],
					nested_component: <OrderLineDetails table_ref={tableRef} row={row} />,
				}))}
			/>
		</Box>
	);
}

/* -------------------------------------------------------------------------- */
/*                          DETERM ORDER STATUS CHIP                          */
/* -------------------------------------------------------------------------- */
function DetermOrderStatusChip({ order_status }) {
	const { t } = useTranslation();

	const determStatusColor = () => {
		switch (order_status) {
			case "active":
			case "completed":
				return "success";

			case "pending":
				return "warning";

			case "replaced":
				return "info";

			case "failed":
			case "ended":
			case "cancelled":
				return "error";
		}
	};

	return (
		<Chip
			variant="outlined"
			label={t(`billing.orders.table.status.${order_status}`)}
			color={determStatusColor()}
		/>
	);
}

/* -------------------------------------------------------------------------- */
/*                        DETERM PRODUCT REDIRECT LINK                        */
/* -------------------------------------------------------------------------- */
function DetermProductRedirectLink({ row }) {
	const { t } = useTranslation();

	switch (row.order_type) {
		case "qemu":
			return (
				<Link href={`/vps/${row.order_type_id}`} passHref style={{ cursor: "pointer" }}>
					<Typography
						color="primary.main"
						sx={{ cursor: "pointer", textDecoration: "underline" }}
						variant="body2"
					>
						VPS: {row.description}
					</Typography>
				</Link>
			);

		case "domain":
			return (
				<Link href={`/domain/${row.order_type_id}`} passHref style={{ cursor: "pointer" }}>
					<Typography
						color="primary.main"
						sx={{ cursor: "pointer", textDecoration: "underline" }}
						variant="body2"
					>
						{t("domain.domain")}: {row.description}
					</Typography>
				</Link>
			);
		case "dadup":
			return (
				<Link href={`/dadup/manage/${row.order_type_id}`} passHref style={{ cursor: "pointer" }}>
					<Typography
						color="primary.main"
						sx={{ cursor: "pointer", textDecoration: "underline" }}
						variant="body2"
					>
						{t("billing.orders.table.order_type.dadup")}: {row.description}
					</Typography>
				</Link>
			);
		case "pbs":
		case "pbs_free":
		case "pbs_sync":
			return (
				<Link href={`/pbs/manage/${row.order_type_id}`} passHref style={{ cursor: "pointer" }}>
					<Typography
						color="primary.main"
						sx={{ cursor: "pointer", textDecoration: "underline" }}
						variant="body2"
					>
						{row.order_type === "pbs_free" ? `PBS ${t("billing.orders.table.free")}: ` : "PBS:"}
						{row.description}
					</Typography>
				</Link>
			);

		default:
			return <Typography>{row.description}</Typography>;
	}
}

/* -------------------------------------------------------------------------- */
/*                             DETERM ORDER TOTAL                             */
/* -------------------------------------------------------------------------- */
function DetermOrderTotal({ row }) {
	// Hooks
	const theme = useTheme();

	// Determ variables
	const orderHasDiscount = row.total !== row.total_with_discount;

	// Format pricing
	const formattedOrderTotal = format_amount_handler(row.total);
	const formattedOrderTotalWDiscount = format_amount_handler(row.total_with_discount);

	return (
		<Box display="flex" alignItems="center">
			{orderHasDiscount ? (
				<Typography
					variant="body2"
					sx={{
						mr: 1,
						textDecoration: "line-through",
						whiteSpace: "nowrap",
					}}
				>
					{formattedOrderTotal}
				</Typography>
			) : null}
			<Box
				sx={{
					alignItems: "center",
					backgroundColor: theme.palette.custom_chip,
					borderRadius: 10,
					display: "flex",
					justifyContent: "center",
					mr: -0.5,
					px: 1.5,
					py: 0.5,
				}}
			>
				{orderHasDiscount ? (
					<Typography variant="body2" color="green" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
						{formattedOrderTotalWDiscount}
					</Typography>
				) : (
					<Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
						{formattedOrderTotal}
					</Typography>
				)}
			</Box>
		</Box>
	);
}

/* -------------------------------------------------------------------------- */
/*                             ORDER LINE DETAILS                             */
/* -------------------------------------------------------------------------- */
function OrderLineDetails({ table_ref, row }) {
	// Hooks
	const { t } = useTranslation();
	const theme = useTheme();
	const orderHasDiscount = row.total !== row.total_with_discount;
	const replacedOrder = row?.replaced_order
		? `B${row.replaced_order.toString().padStart(4, "0")}`
		: null;
	const replacedByOrder = row?.replaced_by_order
		? `B${row.replaced_by_order.toString().padStart(4, "0")}`
		: null;

	return (
		<Box sx={{ m: 1, mb: 3 }}>
			<Box display="flex">
				<Typography variant="h6" gutterBottom component="div">
					{t("billing.orders.table.sub_row.title")} {row.order_code}{" "}
				</Typography>
				<Chip
					label={row.username}
					icon={<PersonIcon />}
					color="primary"
					variant="contained"
					sx={{ ml: 2 }}
					onClick={() => table_ref.current.triggerUpdate({ query: row.username })}
				/>
				{row.replaced_order && (
					<Chip
						label={`${t("billing.orders.table.sub_row.replaces_order")} ${replacedOrder}`}
						icon={<AutorenewIcon />}
						color="company_light"
						variant="contained"
						sx={{ ml: 1 }}
						onClick={() => table_ref.current.triggerUpdate({ query: replacedOrder })}
					/>
				)}
				{row.replaced_by_order && (
					<Chip
						label={`${t("billing.orders.table.sub_row.replaced_by_order")} ${replacedByOrder}`}
						icon={<AutorenewIcon />}
						color="company_light"
						variant="contained"
						sx={{ ml: 1 }}
						onClick={() => table_ref.current.triggerUpdate({ query: replacedByOrder })}
					/>
				)}
				{row.expiry_date && (
					<Chip
						label={`${new Date(row.created_at).toLocaleDateString()} t/m ${new Date(
							row.expiry_date,
						).toLocaleDateString()}`}
						icon={<DateRangeIcon />}
						variant="contained"
						sx={{ ml: 1 }}
					/>
				)}

				{row.purchase_order_number && (
					<Tooltip title={t("billing.orders.table.sub_row.order_purchase_number")}>
						<Chip
							label={row.purchase_order_number}
							icon={<InsertLinkIcon />}
							variant="contained"
							sx={{ cursor: "help", ml: 1 }}
						/>
					</Tooltip>
				)}
			</Box>
			{/* Pbs infor alert */}
			<PbsOrderAlertSection row={row} />
			<Table
				size="small"
				aria-label="purchases"
				sx={{ borderRadius: 2, boxShadow: "rgba(0,0,0, 0.15) 0px 5px 10px" }}
			>
				<TableHead>
					<TableRow>
						<TableCell width="20%">{t("billing.orders.table.sub_row.quantity")}</TableCell>
						<TableCell>{t("billing.orders.table.sub_row.product")}</TableCell>
						<TableCell align="right">{t("billing.orders.table.sub_row.price")}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{row.order_lines.map((orderLine) => {
						const orderLineTotal =
							orderLine.total >= 0 ? format_amount_handler(orderLine.total) : "€ 0,-";
						const orderLineTotalWithDiscount =
							orderLine.total_with_discount >= 0
								? format_amount_handler(orderLine.total_with_discount)
								: "€ 0,-";

						const orderLineHasDiscount =
							orderHasDiscount && orderLine.total !== orderLine.total_with_discount;

						const productUnitTranslationKey = `billing.orders.table.sub_row.unit.${orderLine.product_unit.toLowerCase()}`;

						let productUnitTranslation = orderLine.product_unit;

						if (i18n.exists(productUnitTranslationKey)) {
							productUnitTranslation = t(productUnitTranslationKey);
						}

						return (
							<TableRow
								key={orderLine.product_id}
								sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
							>
								<TableCell component="th" scope="row">
									{orderLine.quantity} {productUnitTranslation}
								</TableCell>
								<TableCell>{orderLine.product_name}</TableCell>
								<TableCell>
									<Box display="flex" justifyContent="flex-end" alignItems="center">
										{orderLineHasDiscount && (
											<Typography
												variant="body2"
												sx={{
													mr: 1,
													textDecoration: "line-through",
													whiteSpace: "nowrap",
												}}
											>
												{orderLineTotal}
											</Typography>
										)}
										<Box
											sx={{
												alignItems: "center",
												backgroundColor: theme.palette.custom_chip,
												borderRadius: 10,
												display: "flex",
												justifyContent: "center",
												mr: -0.5,
												px: 1.5,
												py: 0.5,
											}}
										>
											{orderLineHasDiscount ? (
												<Typography
													variant="body2"
													color="green"
													fontWeight={500}
													sx={{ whiteSpace: "nowrap" }}
												>
													{orderLineTotalWithDiscount}
												</Typography>
											) : (
												<Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
													{orderLineTotal}
												</Typography>
											)}
										</Box>
									</Box>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</Box>
	);
}

/* -------------------------------------------------------------------------- */
/*                               PBS ORDER ALERT                              */
/* -------------------------------------------------------------------------- */
function PbsOrderAlertSection({ row }) {
	// Hooks
	const router = useRouter();
	const [loading, setLoading] = useState(false);

	// Handlers
	const redirectHandler = () => {
		setLoading(true);
		router.push(`/pbs/manage/${row.order_type_id}`);
	};

	if (row.order_type !== "pbs") {
		return null;
	}

	return (
		<Alert
			variant="outlined"
			severity="info"
			sx={{ mb: 2 }}
			action={
				<LoadingButton
					variant="text"
					endIcon={<ArrowForwardIcon />}
					loading={loading}
					loadingPosition="end"
					onClick={redirectHandler}
				>
					<Trans i18nKey="buttons.view" />
				</LoadingButton>
			}
		>
			<Box>
				<Trans i18nKey="billing.orders.table.sub_row.alert_description" />
			</Box>
		</Alert>
	);
}
